<script setup lang="ts">
import LoadingState from "@/components/ui/LoadingState.vue";
// Mission Control: the workspace's day. What's executing on the workers, what
// shipped or failed, where the agents worked, the next decision waiting on a
// person, schedule alerts, a quick start from a Jira key, and the fleet.
import { computed, onMounted, onUnmounted, ref, shallowRef } from "vue";
import { useRouter } from "vue-router";
import { fetchActivity, fetchGraph, fetchJob, listWorkspaces } from "@/api/console";
import type { Activity, GraphSnapshot, JobView, RunRecord } from "@/api/types";
import { useApi } from "@/composables/useApi";
import { useAuthStore } from "@/stores/auth";
import { useRunsStore } from "@/stores/runs";
import { useUiStore } from "@/stores/ui";
import { useWorkspaceStore } from "@/stores/workspace";
import { ago, duration, flowLabel, money, prNumber, repoName } from "@/lib/format";
import { ackRun, runStatus, runTitle } from "@/lib/runState";
import { attemptKind, planPending, runLink, TICKET_KEY_RE, ticketLink } from "@/lib/tickets";
import { modelLabel } from "@/features/launch/launchModel";
import Icon, { type IconName } from "@/components/ui/Icon.vue";
import StatusBadge from "@/components/ui/StatusBadge.vue";
import AlertsBanner from "@/components/alerts/AlertsBanner.vue";
import RepoNotices from "@/components/alerts/RepoNotices.vue";
import CodeMap from "@/components/map/CodeMap.vue";

const WORKERS = 3; // Windmill worker containers on the host (docs/runbooks/windmill-deployment.md)

const auth = useAuthStore();
const runs = useRunsStore();
const ui = useUiStore();
const workspace = useWorkspaceStore();
const router = useRouter();
const { client } = useApi();
const now = ref(Date.now());
const tick = setInterval(() => { now.value = Date.now(); }, 1000);
onUnmounted(() => clearInterval(tick));
onMounted(() => runs.start());

const canLaunch = computed(() => (workspace.me?.role ?? auth.role) !== "viewer");
const firstName = computed(() => {
  const n = workspace.me?.name?.split(" ")[0];
  if (n) return n;
  const local = (auth.email || "").split("@")[0].split(/[._-]/)[0];
  return local ? local[0].toUpperCase() + local.slice(1) : "there";
});
const greeting = computed(() => {
  const h = new Date(now.value).getHours();
  return h < 5 ? "Working late" : h < 12 ? "Good morning" : h < 18 ? "Good afternoon" : "Good evening";
});
const today = computed(() => new Date(now.value).toLocaleDateString(undefined, { weekday: "long", day: "numeric", month: "short" }));

// ── the day in four numbers ──
const executing = computed(() => runs.active.length);
const queued = computed(() => Math.max(0, executing.value - WORKERS));
const pendingPlans = computed(() => runs.runs.filter(planPending));
const prsToday = computed(() => runs.doneToday.filter((r) => r.pr_url).length);
const summary = computed(() => {
  const bits = [executing.value ? `${executing.value} run${executing.value === 1 ? "" : "s"} executing` : "No runs executing"];
  if (runs.doneToday.length) bits.push(`${runs.doneToday.length} finished today`);
  if (pendingPlans.value.length) bits.push(`${pendingPlans.value.length === 1 ? "A decision is" : `${pendingPlans.value.length} decisions are`} waiting on you`);
  else if (runs.attention.length) bits.push(`${runs.attention.length} failure${runs.attention.length === 1 ? "" : "s"} to look at`);
  return bits;
});

// ── where the agents worked (best effort: the card says when it can't show) ──
const activity = shallowRef<Activity | null>(null);
const snapshot = shallowRef<GraphSnapshot | null>(null);
const mapState = ref<"loading" | "ready" | "none" | "error">("loading");
onMounted(async () => {
  try {
    const ws = (await listWorkspaces(client))[0];
    if (!ws) { mapState.value = "none"; return; }
    const [a, g] = await Promise.all([fetchActivity(client, ws.id, 30), fetchGraph(client, ws.id, 3)]);
    activity.value = a;
    snapshot.value = g;
    mapState.value = "ready";
  } catch { mapState.value = "error"; }
});
const footprint = computed(() => new Map((activity.value?.nodes ?? []).map((n) => [n.id, { reads: n.reads, edits: n.edits }])));
const focus = computed(() => (activity.value?.nodes ?? []).slice(0, 12).map((n) => n.id));
const successRate = computed(() => {
  const t = activity.value?.totals;
  return t && t.runs ? Math.round((t.succeeded / t.runs) * 1000) / 10 : null;
});
const mostWorked = computed(() => (activity.value?.nodes ?? []).slice(0, 3).map((n) => n.id.split(/[/:]/).pop() ?? n.id));

// ── the next decision: the oldest plan waiting on a person, else the newest failure ──
const decision = computed<RunRecord | null>(() => {
  const plans = [...pendingPlans.value].sort((a, b) => a.created_at.localeCompare(b.created_at));
  return plans[0] ?? runs.attention[0] ?? null;
});
const decisionJob = shallowRef<JobView | null>(null);
let decisionFor = "";
async function loadDecision() {
  const d = decision.value;
  if (!d || d.job_id === decisionFor) return;
  decisionFor = d.job_id;
  decisionJob.value = null;
  try { const j = await fetchJob(client, d.job_id); if (decisionFor === d.job_id) decisionJob.value = j; } catch { /* the card still says what it is */ }
}
const decisionPoll = setInterval(loadDecision, 3000);
onMounted(loadDecision);
onUnmounted(() => clearInterval(decisionPoll));
const plan = computed(() => (decisionJob.value?.result?.plan ?? null) as { root_cause?: string; steps?: unknown[]; model?: string } | null);
const planReach = computed(() => (decisionJob.value?.result?.plan_impact as { totals?: Record<string, number> } | undefined)?.totals ?? null);
const decisionTitle = computed(() => String(decisionJob.value?.result?.jira_summary ?? ""));

// ── quick start ──
const quick = ref("");
const quickKey = computed(() => quick.value.trim().toUpperCase());
const quickValid = computed(() => TICKET_KEY_RE.test(quickKey.value));
function launch() {
  ui.openLaunch(quickValid.value ? { ticket: quickKey.value } : {});
  quick.value = "";
}

// ── the fleet ──
type Filter = "all" | "working" | "attention" | "finished";
const filter = ref<Filter>("all");
const fleet = computed(() => runs.runs.filter((r) => {
  if (filter.value === "working") return r.status === "running";
  if (filter.value === "attention") return planPending(r) || (r.status === "failure" && !r.canceled_by);
  if (filter.value === "finished") return r.status !== "running";
  return true;
}).slice(0, 24));
function icon(r: RunRecord): IconName {
  if (r.status === "running") return "bolt";
  if (planPending(r)) return "shield";
  if (r.status === "failure") return r.canceled_by ? "stop" : "warning";
  return "check";
}
function agentOf(r: RunRecord): string {
  const kind = attemptKind(r);
  const m = r.model ? modelLabel(r.model) : "";
  const p = r.planner_model ? modelLabel(r.planner_model) : "";
  if (p && m && kind === "fix") return `${p} → ${m}`;
  return m || flowLabel(r.flow);
}
function elapsed(r: RunRecord): string {
  const end = r.status === "running" ? now.value : r.finished_at ? Date.parse(r.finished_at) : null;
  return end ? duration(end - Date.parse(r.created_at)) : "—";
}
function open(r: RunRecord) { void router.push(runLink(r)); }
function started(iso: string) {
  const t = Date.parse(iso);
  return now.value - t < 20 * 3600_000 ? ago(iso, now.value) : new Date(t).toLocaleString(undefined, { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
}
</script>

<template>
  <div class="page mc">
    <header class="page-heading">
      <div>
        <span class="eyebrow">Mission Control / {{ today }}</span>
        <h1>{{ greeting }}, {{ firstName }}.</h1>
        <p>
          <template v-for="(b, i) in summary" :key="b">
            <span v-if="i" class="faint" aria-hidden="true">·</span>
            <span :class="{ live: i === 0 && executing }"><span v-if="i === 0" class="dot" :class="executing ? 'working' : 'idle'" aria-hidden="true" />{{ b }}</span>
          </template>
        </p>
      </div>
      <RouterLink to="/ask" class="btn btn--primary"><Icon name="message" />Ask Majlis</RouterLink>
    </header>

    <AlertsBanner />

    <section class="stats panel" aria-label="Today">
      <div class="stat">
        <div class="stat-label">Runs executing <Icon name="bolt" :size="15" /></div>
        <div class="stat-number mono">{{ executing }}<small> / {{ WORKERS }}</small></div>
        <div class="stat-footer">
          <span class="slots" aria-hidden="true"><i v-for="n in WORKERS" :key="n" :data-on="n <= executing" /></span>
          {{ queued ? `${queued} beyond the ${WORKERS} configured workers` : `of ${WORKERS} configured workers` }}
        </div>
      </div>
      <div class="stat">
        <div class="stat-label">Finished today <Icon name="branch" :size="15" /></div>
        <div class="stat-number mono">{{ runs.doneToday.length }}</div>
        <div class="stat-footer"><span class="good">↗ {{ prsToday }} with a pull request</span></div>
      </div>
      <div class="stat">
        <div class="stat-label">Failed today <Icon name="shield" :size="15" /></div>
        <div class="stat-number mono" :class="{ bad: runs.failedToday.length }">{{ runs.failedToday.length }}</div>
        <div class="stat-footer">
          <template v-if="runs.attention.length"><span class="bad">{{ runs.attention.length }} failure{{ runs.attention.length === 1 ? "" : "s" }}, any day, not yet opened</span></template>
          <template v-else><span class="good">✓</span> every failure has been opened</template>
        </div>
      </div>
      <div class="stat">
        <div class="stat-label">Reported cost today <Icon name="dollar" :size="15" /></div>
        <div class="stat-number mono">${{ runs.spendToday.toFixed(2) }}</div>
        <div class="stat-footer">List-price equivalent, as the runs reported it · the subscription isn't billed per run</div>
      </div>
    </section>

    <div class="hero">
      <section class="panel living">
        <header class="panel-header">
          <div><h2>A living view of your code.</h2><p>Where the agents worked in the last 30 days</p></div>
          <RouterLink to="/map?mode=activity" class="btn btn--ghost btn--sm">Explore map <Icon name="arrow" /></RouterLink>
        </header>
        <div class="living-map">
          <CodeMap v-if="mapState === 'ready' && snapshot" :snapshot="snapshot" mode="activity" :footprint="footprint" :focus-ids="focus" compact />
          <div v-else class="map-empty">
            <LoadingState v-if="mapState === 'loading'">Loading the code map…</LoadingState>
            <p v-else class="muted small">{{ mapState === "none" ? "No code map yet: the nightly indexer maps the repositories configured for it." : "The code map didn't load." }}</p>
          </div>
          <div class="legend mono"><span><i class="read" />Read</span><span><i class="edit" />Edit</span></div>
        </div>
        <footer class="living-foot mono">
          <template v-if="activity">
            <span><b>{{ activity.totals.runs }}</b> runs</span><span><b>{{ activity.totals.tickets }}</b> tickets</span>
            <span><b>{{ activity.totals.prs }}</b> PRs</span><span><b>{{ successRate ?? "—" }}{{ successRate === null ? "" : "%" }}</b> success</span>
            <span class="push"><b>{{ activity.totals.nodes_edited }}</b> files edited</span>
          </template>
          <span v-else class="muted">—</span>
        </footer>
      </section>

      <section class="panel decision" :data-kind="decision ? (planPending(decision) ? 'plan' : 'failure') : 'none'" aria-labelledby="decision-h">
        <template v-if="decision && planPending(decision)">
          <div class="row between"><span class="eyebrow">Your next decision</span><span class="badge" data-tone="attention">◇ Approval needed</span></div>
          <p class="mono small muted where">{{ runTitle(decision) }} / {{ repoName(decision.github_repo) }}</p>
          <h2 id="decision-h">{{ decisionTitle || "A plan is ready for review." }}</h2>
          <p class="muted small lead">{{ plan?.root_cause ?? "The planner's proposal is ready. Review the approach before the agent writes a line of code." }}</p>
          <div class="facts mono small">
            <span>{{ plan?.steps?.length ?? "—" }} step{{ plan?.steps?.length === 1 ? "" : "s" }}</span>
            <span>{{ planReach ? `${planReach.dependent_modules ?? 0} dependents` : "reach not computed" }}</span>
            <span>{{ modelLabel(plan?.model ?? decision.planner_model ?? "") || "planner" }}</span>
          </div>
          <div class="row between">
            <RouterLink :to="ticketLink(decision.jira_ticket ?? '', { run: decision.job_id, view: 'overview' })" class="btn btn--primary"><Icon name="arrow" />Review plan</RouterLink>
            <span class="muted small">No GitHub writes yet</span>
          </div>
        </template>
        <template v-else-if="decision">
          <div class="row between"><span class="eyebrow">Needs a look</span><StatusBadge :run="decision" /></div>
          <p class="mono small muted where">{{ runTitle(decision) }} / {{ repoName(decision.github_repo) }}</p>
          <h2 id="decision-h">{{ flowLabel(decision.flow) }} failed{{ decision.error_step ? ` at ${decision.error_step}` : "" }}.</h2>
          <p class="muted small lead">It stopped {{ ago(decision.finished_at ?? decision.created_at, now) }}. Its activity shows the last events and the error.</p>
          <div class="row">
            <RouterLink :to="runLink(decision, 'activity')" class="btn btn--primary"><Icon name="arrow" />Inspect</RouterLink>
            <button class="btn btn--ghost" @click="runs.ack(decision.job_id, ackRun)">Dismiss</button>
          </div>
        </template>
        <template v-else>
          <span class="eyebrow">Your next decision</span>
          <h2 id="decision-h">Nothing is waiting on you.</h2>
          <p class="muted small lead">Plans that need approval and failures to look at appear here first.</p>
          <RouterLink to="/tickets" class="btn">All tickets <Icon name="arrow" /></RouterLink>
        </template>
      </section>
    </div>

    <form v-if="canLaunch" class="quick" role="search" @submit.prevent="launch">
      <span class="quick-icon" aria-hidden="true"><Icon name="plus" /></span>
      <label for="quick-key" class="quick-label">Start with a ticket</label>
      <input id="quick-key" v-model="quick" class="field mono" placeholder="Paste a Jira key, e.g. DA-2260" autocomplete="off" spellcheck="false" />
      <button class="btn" :class="{ 'btn--primary': quickValid }">{{ quickValid ? `Start on ${quickKey}` : "Configure run" }} <Icon name="arrow" /></button>
      <kbd aria-hidden="true" title="Press N anywhere">N</kbd>
    </form>

    <RepoNotices />

    <section class="fleet" aria-labelledby="fleet-h">
      <div class="section-head">
        <h2 id="fleet-h">Your agent fleet <span class="badge">{{ runs.runs.length }} runs</span></h2>
        <div class="segmented" role="group" aria-label="Filter the fleet">
          <button v-for="f in (['all', 'working', 'attention', 'finished'] as const)" :key="f" :aria-pressed="filter === f" @click="filter = f">
            {{ { all: "All runs", working: "Working", attention: "Attention", finished: "Finished" }[f] }}
          </button>
        </div>
      </div>
      <div class="panel">
        <div v-if="!runs.loaded && !runs.error" class="loading"><div v-for="i in 4" :key="i" class="skeleton" style="height: 52px" /></div>
        <div v-else-if="runs.error && !runs.loaded" class="empty-state">
          <div><div class="state-symbol"><Icon name="warning" /></div><h2>The run log didn't load</h2><p>{{ runs.error }}</p>
            <div class="row"><button class="btn" @click="runs.refresh()"><Icon name="refresh" />Try again</button></div></div>
        </div>
        <div v-else-if="!fleet.length" class="empty-state">
          <div>
            <div class="state-symbol"><Icon name="sparkle" /></div>
            <h2>{{ filter === "all" ? "Quiet on the floor." : "Nothing here." }}</h2>
            <p>{{ filter === "all" ? "Paste a Jira key above, or press N anywhere to start an agent. A new workspace connects its repositories first." : "No run matches this filter." }}</p>
            <div v-if="filter === 'all' && auth.isAdmin" class="row"><RouterLink to="/onboarding" class="btn">Connect a project</RouterLink></div>
          </div>
        </div>
        <div v-else class="table-scroll">
          <table class="table fleet-table">
            <thead><tr><th>Task / repository</th><th>Status</th><th>Agent</th><th>Elapsed</th><th>Spend</th><th><span class="sr-only">Open</span></th></tr></thead>
            <tbody>
              <tr v-for="r in fleet" :key="r.job_id" class="clickable" @click="open(r)">
                <td>
                  <div class="task">
                    <span class="task-icon" :data-tone="runStatus(r).tone" aria-hidden="true"><Icon :name="icon(r)" :size="15" /></span>
                    <div>
                      <RouterLink :to="runLink(r)" class="task-title" @click.stop>{{ runTitle(r) }} · {{ flowLabel(r.flow) }}<template v-if="r.ab_label"> · A/B {{ r.ab_label }}</template></RouterLink>
                      <span class="mono task-sub">{{ repoName(r.github_repo) }} · {{ r.triggered_by.split("@")[0] }}<template v-if="r.trigger_source !== 'manual'"> via {{ r.trigger_source }}</template> · {{ started(r.created_at) }}<template v-if="r.error_step"> · stopped at {{ r.error_step }}</template></span>
                    </div>
                  </div>
                </td>
                <td><StatusBadge :run="r" /><a v-if="r.pr_url" :href="r.pr_url" target="_blank" rel="noopener" class="pr mono" @click.stop>{{ prNumber(r.pr_url) }}</a></td>
                <td class="mono muted">{{ agentOf(r) }}</td>
                <td class="mono">{{ elapsed(r) }}</td>
                <td class="mono">{{ typeof r.cost_usd === "number" ? money(r.cost_usd) : "—" }}</td>
                <td class="go"><span class="icon-btn" aria-hidden="true"><Icon name="arrow" :size="15" /></span></td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
      <div class="fleet-foot mono small faint">
        <span v-if="mostWorked.length">Most worked: {{ mostWorked.join(" · ") }}</span>
        <span>Updated {{ runs.lastFetch ? ago(runs.lastFetch, now) : "—" }}</span>
      </div>
    </section>
  </div>
</template>

<style scoped>
.mc { display: flex; flex-direction: column; gap: var(--space-6); }
.mc .page-heading { margin-bottom: 0; }
.page-heading p .dot.working { color: var(--working); animation: pulse-ring 1.8s var(--ease) infinite; margin-right: var(--space-2); }
.page-heading p .dot.idle { color: var(--faint); margin-right: var(--space-2); }
.stats { display: grid; grid-template-columns: repeat(4, 1fr); }
.stat { padding: var(--space-5) var(--space-6); border-right: 1px solid var(--line); min-width: 0; }
.stat:last-child { border-right: 0; }
.stat-label { display: flex; align-items: center; justify-content: space-between; font-size: var(--text-xs); color: var(--muted); }
.stat-label .icon { opacity: .7; }
.stat-number { font-size: var(--text-2xl); line-height: 1.1; letter-spacing: -1.2px; margin: var(--space-4) 0 var(--space-3); }
.stat-number small { font-size: var(--text-md); color: var(--faint); letter-spacing: -.4px; }
.stat-number.bad { color: var(--failed); }
.stat-footer { display: flex; gap: var(--space-3); align-items: center; font-size: var(--text-xs); color: var(--muted); flex-wrap: wrap; }
.slots { display: inline-flex; gap: var(--space-1); }
.slots i { width: 14px; height: 4px; border-radius: var(--radius-s); background: var(--line); }
.slots i[data-on="true"] { background: var(--working); }
.good { color: var(--done); }
.bad { color: var(--failed); }
.hero { display: grid; grid-template-columns: minmax(0, 1.55fr) minmax(min(100%, 320px), 1fr); gap: var(--space-5); }
.living { display: flex; flex-direction: column; background: var(--graph-bg); }
.living-map { position: relative; height: 300px; }
.map-empty { position: absolute; inset: 0; display: grid; place-items: center; padding: var(--space-5); text-align: center; }
.legend { position: absolute; left: 20px; bottom: 12px; display: flex; gap: var(--space-4); font-size: var(--text-xs); color: var(--muted); pointer-events: none; }
.legend i { display: inline-block; width: 6px; height: 6px; border-radius: 50%; margin-right: var(--space-2); }
.legend .read { background: var(--read); }
.legend .edit { background: var(--edit); }
.living-foot { display: flex; gap: var(--space-5); padding: var(--space-4) var(--space-6); border-top: 1px solid var(--line); font-size: var(--text-xs); color: var(--muted); flex-wrap: wrap; }
.living-foot b { color: var(--ink); font-weight: 500; margin-right: var(--space-2); }
.living-foot .push { margin-left: auto; }
.decision { padding: var(--space-6); display: flex; flex-direction: column; gap: var(--space-4); }
.decision[data-kind="plan"] { border-color: color-mix(in srgb, var(--brand) 40%, var(--line)); }
.decision[data-kind="failure"] { border-color: color-mix(in srgb, var(--failed) 30%, var(--line)); }
.decision h2 { font-size: var(--text-xl); font-weight: 500; letter-spacing: -.6px; line-height: 1.3; }
.between { justify-content: space-between; }
.where { margin-top: calc(-1 * var(--space-2)); }
.lead { display: -webkit-box; -webkit-line-clamp: 4; -webkit-box-orient: vertical; overflow: hidden; }
.facts { display: flex; justify-content: space-between; gap: var(--space-3); padding: var(--space-4) 0; border-top: 1px solid var(--line); border-bottom: 1px solid var(--line); flex-wrap: wrap; }
.quick { display: flex; align-items: center; gap: var(--space-4); }
.quick-icon { display: grid; place-items: center; width: 32px; height: 32px; border: 1px solid var(--line); border-radius: var(--radius-s); color: var(--muted); flex: none; }
.quick-label { font-size: var(--text-sm); color: var(--ink-2); white-space: nowrap; }
.quick .field { flex: 1; min-width: 0; }
.fleet .section-head h2 .badge { margin-left: var(--space-2); }
.loading { display: flex; flex-direction: column; gap: var(--space-3); padding: var(--space-4); }
.fleet-table td { padding-top: var(--space-4); padding-bottom: var(--space-4); }
.clickable { cursor: pointer; }
.task { display: flex; gap: var(--space-4); align-items: center; min-width: 260px; }
.task-icon { display: grid; place-items: center; width: 30px; height: 30px; border-radius: var(--radius-s); border: 1px solid var(--line); flex: none; color: var(--ink-2); }
.task-icon[data-tone="working"] { color: var(--working); }
.task-icon[data-tone="attention"] { color: var(--brand); }
.task-icon[data-tone="failed"] { color: var(--failed); }
.task-icon[data-tone="done"] { color: var(--done); }
.task-title { display: block; font-size: var(--text-sm); color: var(--ink); }
.task-title:hover { color: var(--brand); }
.task-sub { display: block; font-size: var(--text-xs); color: var(--muted); margin-top: var(--space-1); }
.pr { margin-left: var(--space-3); font-size: var(--text-xs); color: var(--brand); }
.go { text-align: right; }
.go .icon-btn { border: 1px solid var(--line); }
.fleet-foot { display: flex; justify-content: space-between; gap: var(--space-4); margin-top: var(--space-3); flex-wrap: wrap; }
@media (max-width: 1100px) {
  .hero { grid-template-columns: 1fr; }
  .stats { grid-template-columns: repeat(2, 1fr); }
  .stat:nth-child(2) { border-right: 0; }
  .stat:nth-child(-n + 2) { border-bottom: 1px solid var(--line); }
}
@media (max-width: 720px) {
  .stats { grid-template-columns: 1fr 1fr; }
  .stat { padding: var(--space-5); }
  .stat-number { font-size: var(--text-xl); }
  .quick { flex-wrap: wrap; }
  .quick-label, .quick kbd, .quick-icon { display: none; }
.quick-label { grid-column: 1 / -1; }
  .fleet-table th:nth-child(3), .fleet-table td:nth-child(3), .fleet-table th:nth-child(5), .fleet-table td:nth-child(5) { display: none; }
  .task { min-width: 0; }
}
.hero { grid-template-columns: minmax(0, 1fr) minmax(0, 1fr); align-items: stretch; }
.decision { order: -1; }
.living-map { height: 200px; }
.stat-label .icon, .slots { display: none; }
.stat-number { font-weight: 500; letter-spacing: -.04em; }
.stat-footer { line-height: 1.5; }
.quick { display: grid; grid-template-columns: minmax(0, 1fr) auto auto; padding: var(--space-5); background: var(--bg-elev); border: 1px solid var(--line); border-radius: var(--radius-m); }
.quick-icon { display: none; }
.quick-label { grid-column: 1 / -1; }
@media (max-width: 1100px) { .hero { grid-template-columns: minmax(0, 1fr); } }
@media (max-width: 520px) {
  .stats { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  .quick { grid-template-columns: minmax(0, 1fr); }
  .quick-label { display: block; }
  .quick .btn { width: 100%; }
}
</style>
